import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faChevronDown, faPencil, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Box,
	ButtonBase,
	Divider,
	IconButton,
	Menu,
	MenuItem,
	Typography,
} from '@mui/material'
import { MouseEvent, ReactNode, useState } from 'react'

import mainColors from '@/styles/mainColors'

export type SelectionOption = {
	id: string
	label: string
	subtitle?: string
}

type SelectionCardProps = {
	icon: IconDefinition
	iconBgColor: string
	iconColor: string
	title: string
	titleAddon?: ReactNode
	subtitle?: string
	options: SelectionOption[]
	selectedId: string
	onSelect: (id: string) => void
	emptyLabel: string
	ariaLabel: string
	compact?: boolean
	variant?: 'default' | 'navbar'
	/** Opens system context menu (left-click on pencil). */
	onSystemMenu?: (event: MouseEvent) => void
	systemMenuAriaLabel?: string
	/** System-level context menu (right-click on shell). */
	onContextMenu?: (event: MouseEvent) => void
	/** Last dropdown action: create a new system. */
	onCreateSystem?: () => void
	createSystemLabel?: string
	disabled?: boolean
	testIdPrefix?: string
}

export const SelectionCard = ({
	icon,
	iconBgColor,
	iconColor,
	title,
	titleAddon,
	subtitle,
	options,
	selectedId,
	onSelect,
	emptyLabel,
	ariaLabel,
	compact = false,
	variant = 'default',
	onSystemMenu,
	systemMenuAriaLabel,
	onContextMenu,
	onCreateSystem,
	createSystemLabel,
	disabled = false,
	testIdPrefix = 'system-selection',
}: SelectionCardProps) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
	const open = Boolean(anchorEl)
	const isNavbar = variant === 'navbar'
	const canOpenMenu = !disabled && (options.length > 0 || !!onCreateSystem)

	const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
		if (!canOpenMenu) return
		setAnchorEl(event.currentTarget)
	}

	const handleClose = () => setAnchorEl(null)

	const handleSelect = (id: string) => {
		onSelect(id)
		handleClose()
	}

	const handleSystemMenu = (event: MouseEvent) => {
		event.preventDefault()
		event.stopPropagation()
		onSystemMenu?.(event)
	}

	const handleCreateSystem = () => {
		handleClose()
		onCreateSystem?.()
	}

	const shellSx = isNavbar
		? {
				display: 'flex',
				width: '100%',
				flex: 1,
				minWidth: 0,
				height: 32,
				alignItems: 'center',
				gap: 0.5,
				pl: 1.25,
				pr: 0.5,
				textAlign: 'left' as const,
				borderRadius: '6px',
				border: '1px solid rgba(255,255,255,0.28)',
				bgcolor: 'rgba(255,255,255,0.08)',
				color: '#fff',
				boxSizing: 'border-box' as const,
				'&:hover': {
					borderColor: 'rgba(255,255,255,0.45)',
					bgcolor: 'rgba(255,255,255,0.14)',
				},
			}
		: {
				display: 'flex',
				flex: 1,
				minWidth: 0,
				minHeight: 0,
				height: compact ? 32 : undefined,
				maxHeight: compact ? 32 : undefined,
				alignItems: 'center',
				gap: 0.5,
				pl: compact ? '9px' : 1.25,
				pr: 0.5,
				py: compact ? 0 : 1,
				textAlign: 'left' as const,
				borderRadius: compact ? '6px' : '8px',
				border: '1px solid #e5e7eb',
				bgcolor: '#fff',
				boxShadow: compact ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
				'&:hover': {
					borderColor: '#d1d5db',
					bgcolor: '#f9fafb',
				},
			}

	const actionIconSx = {
		width: 26,
		height: 26,
		p: 0,
		color: isNavbar ? 'rgba(255,255,255,0.75)' : '#9ca3af',
		'&:hover': {
			color: isNavbar ? '#fff' : '#4b5563',
			bgcolor: isNavbar ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.04)',
		},
	}

	return (
		<>
			<Box sx={shellSx} onContextMenu={onContextMenu}>
				<ButtonBase
					aria-label={ariaLabel}
					aria-haspopup="listbox"
					aria-disabled={disabled || !canOpenMenu}
					aria-expanded={open}
					data-testid={`${testIdPrefix}-trigger`}
					onClick={handleOpen}
					sx={{
						display: 'flex',
						flex: 1,
						minWidth: 0,
						alignItems: 'center',
						gap: compact || isNavbar ? 1 : 1.25,
						py: 0,
						textAlign: 'left',
						borderRadius: '4px',
					}}
				>
					{compact || isNavbar ? (
						<FontAwesomeIcon
							icon={icon}
							color={iconColor}
							style={{ fontSize: 13, flexShrink: 0 }}
						/>
					) : (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								width: 32,
								height: 32,
								flexShrink: 0,
								borderRadius: '5px',
								bgcolor: iconBgColor,
								fontSize: 14,
							}}
						>
							<FontAwesomeIcon icon={icon} color={iconColor} />
						</Box>
					)}
					{compact || isNavbar ? (
						<Box
							sx={{
								minWidth: 0,
								flex: 1,
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								overflow: 'hidden',
							}}
						>
							<Typography
								component="span"
								data-testid={`${testIdPrefix}-title`}
								sx={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									fontSize: isNavbar ? 13 : 13.5,
									fontWeight: 700,
									color: isNavbar ? '#fff' : '#111827',
									lineHeight: 1.2,
									minWidth: 0,
								}}
							>
								{title || emptyLabel}
							</Typography>
							{titleAddon}
							{subtitle ? (
								<Typography
									component="span"
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										fontSize: 12,
										fontWeight: 500,
										color: isNavbar
											? 'rgba(255,255,255,0.55)'
											: '#9ca3af',
										lineHeight: 1.2,
										flexShrink: 0,
										maxWidth: '42%',
									}}
								>
									{subtitle}
								</Typography>
							) : null}
						</Box>
					) : (
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
								<Typography
									data-testid={`${testIdPrefix}-title`}
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										fontSize: 14,
										fontWeight: 600,
										color: '#111827',
										lineHeight: 1.2,
									}}
								>
									{title || emptyLabel}
								</Typography>
								{titleAddon}
							</Box>
							{subtitle ? (
								<Typography
									sx={{
										mt: 0.25,
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										fontSize: 12,
										fontWeight: 500,
										color: '#6b7280',
										lineHeight: 1.2,
									}}
								>
									{subtitle}
								</Typography>
							) : null}
						</Box>
					)}
					<Box
						sx={{
							flexShrink: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							width: 22,
							color: isNavbar ? 'rgba(255,255,255,0.7)' : '#9ca3af',
							fontSize: 11,
						}}
					>
						<FontAwesomeIcon icon={faChevronDown} />
					</Box>
				</ButtonBase>
				{onSystemMenu ? (
					<IconButton
						size="small"
						aria-label={systemMenuAriaLabel}
						data-testid="system-selection-menu"
						onClick={handleSystemMenu}
						disabled={!selectedId}
						sx={actionIconSx}
					>
						<FontAwesomeIcon icon={faPencil} style={{ fontSize: 11 }} />
					</IconButton>
				) : null}
			</Box>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: {
							minWidth: Math.max(anchorEl?.offsetWidth ?? 200, 220),
							mt: 0.5,
						},
					},
				}}
			>
				{options.map((option) => (
					<MenuItem
						key={option.id}
						selected={option.id === selectedId}
						data-testid={`${testIdPrefix}-option-${option.label}`}
						onClick={() => handleSelect(option.id)}
					>
						<Box
							sx={{
								display: 'flex',
								minWidth: 0,
								alignItems: 'center',
								gap: 1.25,
								width: '100%',
							}}
						>
							<FontAwesomeIcon
								icon={icon}
								color={mainColors.danger}
								style={{ fontSize: 13, flexShrink: 0 }}
							/>
							<Typography
								sx={{
									overflow: 'hidden',
									textOverflow: 'ellipsis',
									whiteSpace: 'nowrap',
									fontSize: 14,
									fontWeight: 600,
									minWidth: 0,
									flex: 1,
								}}
							>
								{option.label}
							</Typography>
							{option.subtitle ? (
								<Typography
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										fontSize: 12,
										fontWeight: 500,
										color: '#6b7280',
										flexShrink: 0,
										maxWidth: '42%',
									}}
								>
									{option.subtitle}
								</Typography>
							) : null}
						</Box>
					</MenuItem>
				))}
				{onCreateSystem ? (
					<>
						{options.length > 0 ? <Divider sx={{ my: 0.5 }} /> : null}
						<MenuItem
							data-testid="system-selection-create-system"
							onClick={handleCreateSystem}
						>
							<Box
								sx={{
									display: 'flex',
									minWidth: 0,
									alignItems: 'center',
									gap: 1.25,
									width: '100%',
								}}
							>
								<FontAwesomeIcon
									icon={faPlus}
									color={mainColors.danger}
									style={{ fontSize: 13, flexShrink: 0 }}
								/>
								<Typography
									sx={{
										fontSize: 14,
										fontWeight: 600,
										color: '#111827',
									}}
								>
									{createSystemLabel}
								</Typography>
							</Box>
						</MenuItem>
					</>
				) : null}
			</Menu>
		</>
	)
}
